import React from 'react';
import { HtmlSafe } from 'polaris-coreweb/exports';
import { DealsBlock } from 'Components/organisms/DealsBlock';

/**
 * Formats the deals data to be used in the DealsBlock component.
 *
 * @param {Object} data - The raw data to be formatted.
 * @param {Array} data.vehicleItem - Array of vehicle items.
 * @param {Object} data.config - Configuration object.
 * @param {Object} data.config.image - Image configuration.
 * @param {number} data.config.image.height - Default image height.
 * @param {number} data.config.image.width - Default image width.
 * @param {Object} data.config.tracking - Tracking configuration.
 * @param {Object} data.config.usedTracking - Used tracking configuration.
 * @param {string} data.config.disclaimer - Disclaimer HTML string.
 * @param {string} data.config.title - Title of the deals block.
 * @param {Object} data.extraClassNames - Additional class names.
 * @param {boolean} data.isScrollable - Flag to indicate if the block is scrollable.
 * @param {string} data.type - Type of the deals block.
 * @returns {Object|null} Formatted data object or null if data is invalid.
 */
export const formatDealsData = data => {
  if (!data?.vehicleItem?.length) return null;

  return ({
    content: data.vehicleItem.map(item => ({
      icon: data.config?.icon,
      image: {
        alt: item?.title,
        height: data.config?.image?.height || 202,
        src: item?.imageSrc,
        width: data.config?.image?.width || 360,
      },
      marketplaceLink: {
        extraLabel: data.type === 'newCarDealsBlock' ? (item?.marketplaceLink?.extraLabel || 'Configure now') : '',
        label: data.type === 'usedDealsBlock' ? item?.marketplaceLink?.title : `New ${item?.title}`,
        tracking: data.config?.tracking || {},
        url: item?.marketplaceLink?.url,
      },
      newMarketplaceLink: {
        label: item?.newMarketplaceLink?.title || `New ${item?.title}`,
        tracking: data.config?.tracking || {},
        url: item?.newMarketplaceLink?.url,
      },
      usedMarketplaceLink: {
        label: `Used ${item?.title}`,
        tracking: data.config?.usedTracking || {},
        url: item?.usedMarketplaceLink?.url,
      },
      cashPrice: {
        min: item?.cashPrice,
        prefixText: 'Cash ',
      },
      rrpPrice: {
        min: item?.rrpPrice,
        prefixText: 'RRP ',
      },
      usedPrice: {
        min: item?.usedPrice,
        prefixText: 'Used from ',
      },
      savingPrice: {
        min: item?.rrpSaving,
        postText: ' off RRP\u002A',
        prefixText: 'Avg. savings ',
      },
      financePrice: {
        min: item?.financePrice,
        prefixText: 'From ',
        postText: ' ppm\u002A\u002A',
      },
      techData: item?.techData || [],
      tagline: item?.tagline,
      title: item?.title,
      url: item?.url,
    })),
    disclaimer: data.config?.disclaimer ? <HtmlSafe html={data.config.disclaimer} /> : null,
    extraClassNames: data.extraClassNames,
    isScrollable: data.isScrollable ?? true,
    title: (data.title || data.config?.title) || 'New & used car deals',
    type: data.type || 'commerce',
  });
};

/**
 * Generates the DealsBlock component with formatted data.
 *
 * @param {Object} props - The properties passed to the component.
 * @returns {JSX.Element|null} The DealsBlock component or null if props are invalid.
 */
export const getDealsBlock = (props) => {
  if (!props || Object.keys(props).length === 0) return null;

  const formattedData = formatDealsData(props);
  if (!formattedData) return null;

  return <DealsBlock {...formattedData} />;
};
